import { Input } from 'antd';
import type { BrokerTableData, Status } from './types';
import StatusIndicator from 'components/ui/status-indicator';
import CustomButton from 'components/ui/custom-button';
import { EButtonVariant } from 'components/ui/custom-button/enums';
import CustomTable from 'components/ui/custom-table';
import type { TableColumnsType } from 'antd';
import { AddIcon } from 'assets/icons/add';
import { useNavigate } from 'react-router';
import { ENavigationPath } from 'constants/path';
import { useQuery } from 'react-query';
import usePagination from 'hooks/usePagination';
import { useMemo, useState } from 'react';
import ContentIcon from 'assets/icons/content';
import { brokerApi } from 'services/apis';
import { useTranslation } from 'react-i18next';
import SearchIcon from 'assets/icons/search';

export default function BrokerTable() {
  const [inputText, setInputText] = useState('');
  const [searchText, setSearchText] = useState('');
  const { currentPage, pageSize, onPageChange, onShowSizeChange } =
    usePagination();

  const { t } = useTranslation();

  const columns: TableColumnsType<BrokerTableData> = useMemo(
    () => [
      {
        key: 'index',
        title: <ContentIcon />,
        dataIndex: 'index',
        width: '1%',
        align: 'center',
        render: (index: number) => index + 1,
      },
      {
        key: 'group',
        title: t('broker.brokerGroup'),
        dataIndex: 'group',
        width: '15%',
        align: 'left',
      },
      {
        key: 'name',
        title: t('broker.brokerName'),
        dataIndex: 'name',
        width: '15%',
        align: 'left',
        render: (text: string) => (
          <div
            style={{
              wordBreak: 'break-word',
              whiteSpace: 'pre-wrap',
              maxWidth: '15vw',
            }}
          >
            {text}
          </div>
        ),
      },
      {
        key: 'legalEntityName',
        title: t('broker.brokerLegalEntityName'),
        dataIndex: 'legalEntityName',
        width: '15%',
        align: 'left',
        render: (text: string) => (
          <div
            style={{
              wordBreak: 'break-word',
              whiteSpace: 'pre-wrap',
              maxWidth: '15vw',
            }}
          >
            {text}
          </div>
        ),
      },
      {
        key: 'country',
        title: t('general.country'),
        dataIndex: 'country',
        width: '15%',
        align: 'left',
      },
      {
        key: 'tobaDone',
        title: t('broker.hasTobaDone'),
        dataIndex: 'tobaDone',
        width: '15%',
        align: 'left',
      },
      {
        key: 'kycDone',
        title: t('broker.hasKycDone'),
        dataIndex: 'kycDone',
        width: '15%',
        align: 'left',
      },
      {
        key: 'status',
        title: t('general.status'),
        dataIndex: 'status',
        width: '15%',
        align: 'left',
        render: (text: Status) => <StatusIndicator status={text} />,
      },
    ],
    [t]
  );

  const {
    data: { items: brokerData, pagination: { total } } = {
      items: [],
      pagination: { total: 0 },
    },
  } = useQuery(['brokers', pageSize, currentPage, searchText], async () => {
    const { items, pagination } = await brokerApi.filterBrokers({
      page: currentPage,
      pageSize,
      searchText: searchText,
    });
    return {
      items: items.map((broker, index) => ({
        index: pageSize * (currentPage - 1) + index,
        key: broker.brokerId,
        group: broker.brokerGroup?.brokerGroupName || '',
        name: broker.name,
        country: broker.country,
        tobaDone: broker.hasToba,
        kycDone: broker.hasKyc,
        status: broker.status,
        brokerId: broker.brokerId,
        legalEntityName: broker.legalEntityName,
      })),
      pagination,
    };
  });
  const navigate = useNavigate();

  const handleSearchTrigger = () => setSearchText(inputText);

  return (
    <div className="w-full py-4">
      <div className="flex justify-between mb-4 items-center">
        <div className="text-2xl font-medium">{t('brokers.selectBroker')}</div>
        <CustomButton
          className="w-1/5"
          variant={EButtonVariant.PRIMARY}
          label="Create New Record"
          prefixIcon={<AddIcon />}
          onClick={() => navigate(ENavigationPath.NEW_BROKER)}
        />
      </div>
      <div className="mb-4 flex items-center gap-2">
        <Input
          placeholder="Search Broker if exists"
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          className="flex-grow py-[8px]"
          prefix={<SearchIcon />}
          onPressEnter={handleSearchTrigger}
        />
        <CustomButton
          className="w-2/12 py-[8px]"
          variant={EButtonVariant.PRIMARY}
          label={t('general.search')}
          onClick={handleSearchTrigger}
        />
      </div>

      <CustomTable
        currentPage={currentPage}
        pageSize={pageSize}
        onPageChange={onPageChange}
        onShowSizeChange={onShowSizeChange}
        columns={columns}
        dataSource={brokerData || []}
        total={total || 0}
        onRow={(record) => ({
          onClick: () =>
            navigate(
              ENavigationPath.UPDATE_BROKER.replace(':id', record.brokerId)
            ),
          className: 'cursor-pointer hover:bg-gray-50',
        })}
      />
    </div>
  );
}
